import { ProjectionScenario, ProjectionsData, ScenarioDetails } from '../types/projections';
import { formatCurrency } from './performanceMetrics';

interface BaseData {
  leadsPerMonth: number;
  newClientsPerMonth: number;
  averageProjectValue: number;
  marketingInvestment: number;
  totalAcquisitionCosts: number;
  conversionRate: number;
}

function calculateMonthlyInvestments(baseInvestment: number, monthlyIncreasePercent: number): {
  monthlyInvestments: number[];
  additionalTotal: number;
} {
  const monthlyInvestments: number[] = [];
  let currentInvestment = baseInvestment;
  let additionalTotal = 0;

  for (let i = 0; i < 3; i++) {
    const thisMonthInvestment = currentInvestment * (1 + monthlyIncreasePercent / 100);
    const additionalAmount = thisMonthInvestment - currentInvestment;
    
    monthlyInvestments.push(thisMonthInvestment);
    additionalTotal += additionalAmount;
    
    currentInvestment = thisMonthInvestment;
  }

  return {
    monthlyInvestments,
    additionalTotal
  };
}

function calculateScenario(
  baseData: BaseData,
  monthlyInvestment: number,
  month: number,
  costPerLead: number
): ProjectionScenario {
  // Calculate additional investment and leads
  const additionalInvestment = monthlyInvestment - baseData.marketingInvestment;
  const additionalLeads = costPerLead > 0 
    ? Math.floor(additionalInvestment / costPerLead)
    : 0;
  
  // Calculate total leads for this month (base leads + additional leads)
  const totalLeads = baseData.leadsPerMonth + additionalLeads;
  
  // Calculate conversions using the actual conversion rate from baseData
  const conversionRate = baseData.conversionRate / 100;
  const additionalConversions = Math.floor(additionalLeads * conversionRate);
  const totalConversions = baseData.newClientsPerMonth + additionalConversions;
  
  // Calculate revenue based on total conversions and average project value
  const revenue = totalConversions * baseData.averageProjectValue;
  
  // Calculate costs
  const nonMarketingCosts = baseData.totalAcquisitionCosts - baseData.marketingInvestment;
  const totalCosts = monthlyInvestment + nonMarketingCosts;
  
  // Calculate CAC and ROI
  const cac = totalConversions > 0 ? totalCosts / totalConversions : totalCosts;
  const roi = totalCosts > 0 ? ((revenue - totalCosts) / totalCosts) * 100 : 0;

  return {
    month: month + 1,
    revenue,
    costs: totalCosts,
    roi,
    leads: totalLeads,
    conversions: totalConversions,
    cac
  };
}

function getScenarioDetails(
  increase: number, 
  scenarios: ProjectionScenario[], 
  additionalTotal: number,
  costPerLead: number,
  baseData: BaseData
): ScenarioDetails {
  // Calculate total additional leads across 3 months
  const additionalLeads = costPerLead > 0 
    ? Math.floor(additionalTotal / costPerLead)
    : 0;
  
  // Calculate additional new clients using baseData's conversion rate
  const additionalClients = Math.floor(additionalLeads * (baseData.conversionRate / 100));
  
  // Calculate base clients for 3 months
  const baseClientsFor3Months = baseData.newClientsPerMonth * 3;
  
  // Calculate total clients including additional clients from increased investment
  const totalClients = baseClientsFor3Months + additionalClients;
  
  // Calculate total revenue for 3 months using the total number of clients
  const totalRevenue = totalClients * baseData.averageProjectValue;
  
  const monthlyLeads = Math.floor(additionalLeads / 3);
  const lastScenario = scenarios[scenarios.length - 1] || { roi: 0 };

  return {
    name: `Cenário ${increase / 10}: ${increase}% de aumento`,
    increase,
    color: increase === 10 ? '#FF6B6B' : increase === 20 ? '#4ECDC4' : '#45B7D1',
    riskLevel: (increase === 10 ? 'Baixo' : increase === 20 ? 'Médio' : 'Alto') as 'Baixo' | 'Médio' | 'Alto',
    totalRevenue,
    advantages: [
      `Crescimento estimado de ${monthlyLeads} leads por mês`,
      `Total projetado de ${totalClients} clientes em 3 meses`,
      `Ganho adicional de ${additionalClients} cliente(s) em 3 meses`,
      `ROI médio de ${lastScenario.roi.toFixed(1)}%`
    ],
    disadvantages: [
      `Investimento extra de ${formatCurrency(additionalTotal)} ao longo de 3 meses`,
      'Necessidade de ajustes operacionais para acomodar o crescimento',
      increase === 30 ? 'Alto risco operacional' : 'Exige planejamento estratégico'
    ]
  };
}

export function generateProjections(baseData: BaseData): ProjectionsData {
  const costPerLead = baseData.leadsPerMonth > 0 
    ? baseData.marketingInvestment / baseData.leadsPerMonth 
    : baseData.marketingInvestment;

  const conservative = calculateMonthlyInvestments(baseData.marketingInvestment, 10);
  const moderate = calculateMonthlyInvestments(baseData.marketingInvestment, 20);
  const aggressive = calculateMonthlyInvestments(baseData.marketingInvestment, 30);

  const conservativeScenarios = conservative.monthlyInvestments.map((investment, i) => 
    calculateScenario(baseData, investment, i, costPerLead));
  const moderateScenarios = moderate.monthlyInvestments.map((investment, i) => 
    calculateScenario(baseData, investment, i, costPerLead));
  const aggressiveScenarios = aggressive.monthlyInvestments.map((investment, i) => 
    calculateScenario(baseData, investment, i, costPerLead));

  const scenarios = {
    conservative: conservativeScenarios,
    moderate: moderateScenarios,
    aggressive: aggressiveScenarios
  };

  const details = {
    conservative: getScenarioDetails(10, conservativeScenarios, conservative.additionalTotal, costPerLead, baseData),
    moderate: getScenarioDetails(20, moderateScenarios, moderate.additionalTotal, costPerLead, baseData),
    aggressive: getScenarioDetails(30, aggressiveScenarios, aggressive.additionalTotal, costPerLead, baseData)
  };

  return { scenarios, details };
}